import { useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { InfoScreenDevicePendingScreen } from "@/pages/info-screen/components/InfoScreenDevicePendingScreen";
import { InfoScreenModuleRotator } from "@/pages/info-screen/components/InfoScreenModuleRotator";
import { CampsiteBrandBar } from "@/components/branding/CampsiteBrandBar";
import { useBranding } from "@/lib/branding/useBranding";
import { InfoScreenState } from "@/pages/info-screen/components/InfoScreenState";
import { useInfoScreenConfigStream } from "@/pages/info-screen/useInfoScreenConfigStream";
import { useInfoScreenDevice } from "@/pages/info-screen/useInfoScreenDevice";
import { useInfoScreenDisplay } from "@/pages/info-screen/useInfoScreenDisplay";
import { useInfoScreenEvents } from "@/pages/info-screen/useInfoScreenEvents";
import "@/pages/guest-tablet/guest-tablet.css";
import "./info-screen.css";

export function InfoScreenPage() {
  const { siteName: fallbackSiteName, logoUrl: fallbackLogoUrl } = useBranding();
  const { displayCode = "lobby-1" } = useParams<{ displayCode: string }>();
  const imageIndexByPageRef = useRef<Record<string, number>>({});
  const {
    deviceStatus,
    loading: deviceLoading,
    error: deviceError,
    isApproved,
    refresh: refreshDevice,
  } = useInfoScreenDevice(displayCode);
  const { data, error, loading, reload } = useInfoScreenDisplay(displayCode, {
    enabled: isApproved,
  });
  const { data: eventsData, reload: reloadEvents } = useInfoScreenEvents(displayCode, {
    enabled: isApproved,
  });

  const onConfigChanged = useCallback(() => {
    void reload();
    void reloadEvents();
  }, [reload, reloadEvents]);

  const contentRevision = data?.content.revision;
  useEffect(() => {
    imageIndexByPageRef.current = {};
  }, [contentRevision]);

  useInfoScreenConfigStream(displayCode, {
    enabled: isApproved,
    onConfigChanged,
  });

  if (!isApproved) {
    return (
      <div className="guest-tablet-root" data-kiosk="info-screen">
        <InfoScreenDevicePendingScreen
          status={
            deviceStatus
              ? {
                  status: deviceStatus.status,
                  device_id: deviceStatus.device_id,
                  workstation_code: deviceStatus.display_code,
                  workstation_label: deviceStatus.display_label,
                  device_token_short: deviceStatus.device_token_short,
                  message: deviceStatus.message,
                }
              : null
          }
          loading={deviceLoading}
          error={deviceError}
          onRetry={() => void refreshDevice()}
        />
      </div>
    );
  }

  return (
    <div className="guest-tablet-root" data-kiosk="info-screen">
      <div className="info-screen-shell">
        {loading && !data ? (
          <InfoScreenState
            loading
            title="Informationen werden geladen"
            subtitle="Einen Moment bitte – die Anzeige wird vorbereitet."
          />
        ) : null}

        {error ? (
          <div className="info-screen-error-banner" role="alert">
            <span>{error}</span>
            <button type="button" onClick={() => void reload()}>
              Erneut versuchen
            </button>
          </div>
        ) : null}

        {data ? (
          <>
            <div className="info-screen-brand-bar pointer-events-none absolute left-0 right-0 top-0 z-20 flex justify-center bg-gradient-to-b from-black/50 to-transparent px-4 pt-3 pb-8">
              <CampsiteBrandBar
                siteName={data.site_name?.trim() || fallbackSiteName}
                logoUrl={data.logo_url ?? fallbackLogoUrl}
                className="text-white [&_span]:text-white [&_img]:drop-shadow-md"
                compact
              />
            </div>
            <InfoScreenModuleRotator
              content={data.content}
              modules={eventsData?.modules ?? []}
              eventsPayload={eventsData}
              imageIndexByPageRef={imageIndexByPageRef}
            />
          </>
        ) : null}
      </div>
    </div>
  );
}
